const ARABIC_RE = /[؀-ۿ]/;
const LATIN_RE = /[A-Za-z]/;

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /\+?\s?\d[\d\s().-]{6,}\d/g;
const TLDS = 'com|net|org|edu|gov|io|co|sa|ae|cn|uk|us|info|biz|me|app|dev|tech|ai';
const WEBSITE_RE = new RegExp(
  `https?:\\/\\/\\S+|www\\.[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.[a-z]{2,}\\S*|\\b[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.(?:${TLDS})\\b`,
  'gi'
);
const MOBILE_RE = /\b(mob(ile)?|cell|m|whatsapp|wechat)\b/i;
const GENERIC_MAILBOX_RE = /^(info|contact|sales|support|hello|admin|office|mail|enquiries|inquiry|hr|service)@/i;
const GENERIC_DOMAINS = /^(gmail|yahoo|hotmail|outlook|icloud|live|msn|aol|proton|protonmail)\./i;

const TITLE_EN = /\b(manager|manger|director|engineer|consultant|specialist|coordinator|analyst|developer|architect|designer|executive|officer|head|lead|supervisor|president|vice|ceo|cto|cfo|coo|founder|co-?founder|partner|advisor|adviser|associate|representative|salesman|saleswoman|sales|marketing|technician|administrator|accountant|assistant|secretary|owner|chairman|principal|agent|attorney|lawyer|doctor|professor)\b/i;
const TITLE_AR = /(مدير|مندوب|مهندس|رئيس|مستشار|محاسب|مسؤول|مسئول|مشرف|أخصائي|اخصائي|موظف|نائب|فني|مبيعات|مساعد|منسق|مطور|محلل|شريك|مؤسس)/;
const COMPANY_EN = /\b(co|ltd|llc|inc|corp|corporation|company|gmbh|est|establishment|group|holding|holdings|technology|technologies|solutions|services|trading|contracting|industries|industrial|enterprises|energy|printing|press|systems|international|consulting|logistics|media|software|telecom|bank|agency|studio|labs?|partners)\b/i;
const COMPANY_AR = /(شركة|مؤسسة|مطابع|مجموعة|للمقاولات|للتجارة)/;
const ADDRESS_EN = /\b(street|st|road|rd|ave|avenue|blvd|boulevard|lane|drive|district|dist|zone|city|state|country|zip|postal|p\.?\s?o\.?\s?box|box|building|bldg|floor|tower|plaza|suite|riyadh|jeddah|jiddah|dammam|khobar|makkah|madinah|saudi|arabia|ksa|kingdom|dubai|uae|china|usa|uk)\b/i;
const ADDRESS_AR = /(المملكة|الرياض|جدة|الدمام|الخبر|مكة|(^|\s|-)(حي|شارع|طريق|مبنى|برج)(\s|$))/;

const wordCount = (text) => text.trim().split(/\s+/).length;
const bottom = (line) => line.y + line.height;

// Same text block: roughly the same left edge and touching/overlapping vertically.
const isAdjacent = (a, b, maxDx) => {
  if (Math.abs(a.x - b.x) > maxDx) return false;
  const gap = Math.max(a.y, b.y) - Math.min(bottom(a), bottom(b));
  return gap <= 0.8 * Math.max(a.height, b.height);
};

// The name sits right above the job title and roughly left-aligned with it;
// logos and taglines are usually further away in one or both directions.
const nearestAbove = (title, candidates) => {
  const score = (l) => Math.max(0, title.y - bottom(l)) + 0.5 * Math.abs(l.x - title.x);
  return candidates
    .filter((l) => l.y < title.y)
    .sort((a, b) => score(a) - score(b))[0];
};

const domainLabel = (email) => {
  if (!email) return '';
  const domain = email.split('@')[1].toLowerCase();
  if (GENERIC_DOMAINS.test(domain)) return '';
  const skip = ['com', 'net', 'org', 'edu', 'gov', 'www', 'mail'];
  return domain
    .split('.')
    .filter((p) => p.length > 2 && !skip.includes(p))
    .sort((a, b) => b.length - a.length)[0] || '';
};

// "future-cards" -> "Future Cards"
const companyFromDomain = (label) =>
  label
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const matchesDomain = (text, label) => {
  const norm = text.toLowerCase().replace(/[^a-z0-9]/g, '');
  const target = label.replace(/[^a-z0-9]/g, '');
  return norm.length >= 4 && target.length >= 4 && (target.includes(norm) || norm.includes(target));
};

const sortByPosition = (lines) => [...lines].sort((a, b) => a.y - b.y || a.x - b.x);
const joinText = (lines, sep) => sortByPosition(lines).map((l) => l.text).join(sep);

// Pull name / title / company / address out of the lines of ONE script
// (Latin or Arabic) so the two languages of a bilingual card don't mix.
const analyzeScript = (allCands, arabic, label, maxDx) => {
  const TITLE = arabic ? TITLE_AR : TITLE_EN;
  const COMPANY = arabic ? COMPANY_AR : COMPANY_EN;
  const ADDRESS = arabic ? ADDRESS_AR : ADDRESS_EN;
  const stripAddressLabel = (t) => t.replace(/^\s*(add(ress)?|addr)\s*[:.-]\s*/i, '');

  // Text printed 3+ times is a background pattern (e.g. a faint repeated "ISO"), never a field.
  const normalized = (l) => l.text.toLowerCase().replace(/\s+/g, ' ');
  const repeats = allCands.reduce((m, l) => m.set(normalized(l), (m.get(normalized(l)) || 0) + 1), new Map());
  const cands = allCands.filter((l) => repeats.get(normalized(l)) < 3);

  const addressSeeds = cands.filter((l) => ADDRESS.test(stripAddressLabel(l.text)) && !COMPANY.test(l.text));

  // A card can carry several lines with a title word (a logo reading "ISO Consultant" next to
  // "Business Development Director"). The fuller line is the real job title.
  const titleScore = (l) => (l.text.match(new RegExp(TITLE.source, 'gi')) || []).length + (wordCount(l.text) > 1 ? 1 : 0);
  const titleLine = cands
    .filter((l) => !addressSeeds.includes(l) && TITLE.test(l.text) && wordCount(l.text) <= 8)
    .sort((a, b) => titleScore(b) - titleScore(a) || a.y - b.y || a.x - b.x)[0];

  const free = (l) => l !== titleLine && !addressSeeds.includes(l);
  let companySeed = null;
  if (!arabic && label) companySeed = cands.find((l) => free(l) && matchesDomain(l.text, label));
  if (!companySeed) {
    companySeed = cands
      .filter((l) => free(l) && COMPANY.test(l.text) && !l.text.includes(';') && wordCount(l.text) <= 6)
      .sort((a, b) => b.height - a.height)[0];
  }

  // Pick the name before merging company lines, so a name printed directly
  // under the logo isn't swallowed into the company.
  const nameCands = cands.filter(
    (l) => free(l) && l !== companySeed && wordCount(l.text) <= 4 && !/\d/.test(l.text) && !/[;,]/.test(l.text) && !COMPANY.test(l.text) && !TITLE.test(l.text)
  );
  const nameLine = (titleLine && nearestAbove(titleLine, nameCands)) || sortByPosition(nameCands)[0];

  const companyLines = companySeed ? [companySeed] : [];
  const isBlockLine = (l) => !l.text.includes(';') && wordCount(l.text) <= 4 && !/\d/.test(l.text);
  for (let grew = !!companySeed; grew; ) {
    grew = false;
    cands.forEach((l) => {
      if (!free(l) || l === nameLine || companyLines.includes(l) || !isBlockLine(l)) return;
      if (companyLines.some((c) => isAdjacent(c, l, maxDx))) {
        companyLines.push(l);
        grew = true;
      }
    });
  }

  // Address continues onto following lines that have no keyword of their own.
  const addressLines = [...addressSeeds];
  for (let grew = addressSeeds.length > 0; grew; ) {
    grew = false;
    cands.forEach((l) => {
      if (!free(l) || l === nameLine || companyLines.includes(l) || addressLines.includes(l) || l.text.includes(';')) return;
      if (addressLines.some((a) => isAdjacent(a, l, maxDx))) {
        addressLines.push(l);
        grew = true;
      }
    });
  }

  return {
    name: nameLine ? nameLine.text : '',
    position: titleLine ? titleLine.text : '',
    company: joinText(companyLines, ' ').replace(/\s+([.,])/g, '$1'),
    address: sortByPosition(addressLines).map((l) => stripAddressLabel(l.text)).join(', '),
  };
};

// Convert a flat OCR read result ({ lines, imageWidth, imageHeight }) into the
// editable business-card fields the UI works with.
export const parseExtractedData = (ocrReadResult) => {
  const imageWidth = ocrReadResult?.imageWidth || 600;
  const imageHeight = ocrReadResult?.imageHeight || 350;

  const lines = sortByPosition(
    (ocrReadResult?.lines || [])
      .filter((l) => l.text && l.text.trim().length > 1)
      .map((l) => ({ ...l, text: l.text.trim() }))
  );

  const elements = lines.map((line) => ({
    text: line.text,
    x: line.x,
    y: line.y,
    width: line.width,
    height: line.height,
    xPercent: (line.x / imageWidth) * 100,
    yPercent: (line.y / imageHeight) * 100,
    fontSize: Math.max(10, line.height * 0.8),
  }));

  // Contact details are matched anywhere in a line, which also drops labels like "Tel:" / "E-mail:".
  const emails = [];
  const websites = [];
  const phones = [];
  const remaining = [];
  lines.forEach((line) => {
    let rest = line.text;
    let claimed = false;

    const foundEmails = rest.match(EMAIL_RE) || [];
    if (foundEmails.length) {
      emails.push(...foundEmails);
      rest = rest.replace(EMAIL_RE, ' ');
      claimed = true;
    }

    const foundSites = (rest.match(WEBSITE_RE) || []).map((s) => s.replace(/[.,;]+$/, ''));
    if (foundSites.length) {
      websites.push(...foundSites);
      rest = rest.replace(WEBSITE_RE, ' ');
      claimed = true;
    }

    const foundPhones = (rest.match(PHONE_RE) || []).filter((p) => {
      const digits = p.replace(/\D/g, '').length;
      return digits >= 8 && digits <= 15;
    });
    if (foundPhones.length) {
      foundPhones.forEach((p) => phones.push({ value: p.replace(/\s+/g, ''), mobile: MOBILE_RE.test(line.text) }));
      claimed = true;
    }

    if (!claimed) {
      const text = line.text.replace(/^[^\p{L}\p{N}]+/u, '');
      if (text.length > 1) remaining.push({ ...line, text });
    }
  });

  const email = emails.find((e) => !GENERIC_MAILBOX_RE.test(e)) || emails[0] || '';
  const primaryPhone = phones.find((p) => p.mobile) || phones[0];
  const otherPhones = phones.filter((p) => p !== primaryPhone).map((p) => p.value);

  const maxDx = Math.max(60, imageWidth * 0.05);
  const label = domainLabel(email);
  const latin = analyzeScript(
    remaining.filter((l) => !ARABIC_RE.test(l.text) && LATIN_RE.test(l.text)),
    false,
    label,
    maxDx
  );
  const arabic = analyzeScript(remaining.filter((l) => ARABIC_RE.test(l.text)), true, '', maxDx);

  // Latin is the primary language unless the card is Arabic-only (a lone Latin
  // logo then shouldn't win just because it's the only Latin line).
  const primary = latin.position || !arabic.position ? latin : arabic;

  // No company line on the card: fall back to the email domain and mark it as a guess.
  const company = primary.company || companyFromDomain(label);
  const guessed = !primary.company && company ? ['company'] : [];

  return {
    name: primary.name || arabic.name || latin.name || '',
    company,
    guessed,
    rotation: ocrReadResult?.rotation || 0,
    position: primary.position,
    email,
    phone: primaryPhone ? primaryPhone.value : '',
    website: websites.join(', '),
    address: primary.address,
    notes: otherPhones.length ? `Other phone: ${otherPhones.join(', ')}` : '',
    arabic,
    elements,
    image_width: imageWidth,
    image_height: imageHeight,
    extracted_text: lines.map((l) => l.text).join('\n'),
  };
};
